// EllipseOverlay.jsx
import { useMemo } from "react";
import { Polygon, Polyline, CircleMarker, Tooltip } from "react-leaflet";

/** 단일 포인트를 [lat,lng]로 정규화 */
function toLatLngTuple(p) {
  if (Array.isArray(p) && p.length >= 2) {
    const a = Number(p[0]), b = Number(p[1]);
    if (Number.isFinite(a) && Math.abs(a) <= 90 && Number.isFinite(b) && Math.abs(b) <= 180) return [a, b];
    if (Number.isFinite(a) && Math.abs(a) <= 180 && Number.isFinite(b) && Math.abs(b) <= 90) return [b, a];
    return null;
  }
  if (p && typeof p === "object") {
    const lat = p.lat ?? p.latitude ?? p.y;
    const lng = p.lng ?? p.lon ?? p.long ?? p.longitude ?? p.x;
    const A = Number(lat), B = Number(lng);
    if (Number.isFinite(A) && Math.abs(A) <= 90 && Number.isFinite(B) && Math.abs(B) <= 180) return [A, B];
    const A2 = Number(lng), B2 = Number(lat);
    if (Number.isFinite(A2) && Math.abs(A2) <= 90 && Number.isFinite(B2) && Math.abs(B2) <= 180) return [A2, B2];
    return null;
  }
  if (typeof p === "string") {
    const parts = p.trim().split(/[,\s]+/);
    if (parts.length >= 2) {
      const a = Number(parts[0]), b = Number(parts[1]);
      if (Number.isFinite(a) && Math.abs(a) <= 90 && Number.isFinite(b) && Math.abs(b) <= 180) return [a, b];
      if (Number.isFinite(a) && Math.abs(a) <= 180 && Math.abs(b) <= 90) return [b, a];
    }
  }
  return null;
}

/** GeoJSON Polygon/LineString → points 배열 추출 */
function extractPointsFromGeoJSON(geo) {
  if (!geo || typeof geo !== "object" || !geo.type) return null;
  if (geo.type === "Polygon" && Array.isArray(geo.coordinates)) return geo.coordinates[0] ?? [];
  if (geo.type === "LineString" && Array.isArray(geo.coordinates)) return geo.coordinates;
  return null;
}

/**
 * props:
 *  - points: 납작 배열([lng,lat,...]) | 튜플 배열 | 객체 배열 | 문자열 | GeoJSON
 *  - strokeColor?: string   (폴리곤 선색, 기본 "#FFC000")
 *  - strokeWeight?: number  (폴리곤 선두께, 기본 2)
 *  - fillOpacity?: number   (폴리곤 채움 투명도, 기본 0.75)
 *  - visible?: boolean      (기본 true)
 *  - onClick?: function
 *  - latitude_dd?: number   // 중심 위도
 *  - longitude_dd?: number  // 중심 경도
 *  - radius_m?: number      // 반지름(m)
 *  - radiusBearingDeg?: number // 반지름 표시 방위각(기본 90° = 동쪽)
 *  - showCenter?: boolean   // 중심점 표시 (기본 true)
 *  - showRadius?: boolean   // 반지름 선 표시 (기본 true)
 *  - radiusColor?: string   // 반지름 선/점 색 (기본 "#ff2d2d")
 */
export default function EllipseOverlay({
  points = [],
  strokeColor = "#FFC000",
  strokeWeight = 2,
  fillOpacity = 0.75,
  visible = true,
  onClick,

  latitude_dd,
  longitude_dd,
  radius_m,
  radiusBearingDeg = 90,
  showCenter = true,
  showRadius = true,
  radiusColor = "#ff2d2d",
  showRadiusLabel = true,
  radiusLabel,              // ex) "r = 150 m" (미지정 시 자동 생성)
  labelDigits = 0,          // 소수점 자릿수 (기본 0)
}) {
  // 1) 폴리곤 정규화 (항상 호출)
  const positions = useMemo(() => {
    const raw =
      (points && typeof points === "object" && points.type && extractPointsFromGeoJSON(points)) ||
      points;
    if (!raw) return [];

    let arr = raw;
    if (typeof raw === "string") {
      const nums = raw.trim().split(/[,\s]+/).map(Number).filter(Number.isFinite);
      arr = nums;
    }

    if (Array.isArray(arr) && arr.length >= 4 && arr.every(v => Number.isFinite(Number(v)))) {
      const nums = arr.map(Number);
      const norm = [];
      for (let i = 0; i < nums.length - 1; i += 2) {
        const lng = nums[i], lat = nums[i + 1];
        if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) norm.push([lat, lng]);
      }
      if (norm.length >= 3) {
        const [fLat, fLng] = norm[0], [lLat, lLng] = norm[norm.length - 1];
        if (fLat !== lLat || fLng !== lLng) norm.push([fLat, fLng]);
        return norm;
      }
      return [];
    }

    if (Array.isArray(arr)) {
      const norm = [];
      for (const p of arr) {
        const t = toLatLngTuple(p);
        if (t) norm.push(t);
      }
      if (norm.length >= 3) {
        const [fLat, fLng] = norm[0], [lLat, lLng] = norm[norm.length - 1];
        if (fLat !== lLat || fLng !== lLng) norm.push([fLat, fLng]);
        return norm;
      }
    }

    return [];
  }, [points]);

  // 2) 중심/반지름 관련 계산 (항상 호출)
  const center = useMemo(() => {
    const lat = Number(latitude_dd);
    const lng = Number(longitude_dd);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
  }, [latitude_dd, longitude_dd]);

  const radiusTip = useMemo(() => {
    if (!center || !Number.isFinite(radius_m) || radius_m <= 0) return null;

    const R = 6371000;
    const δ = radius_m / R;
    const θ = (radiusBearingDeg * Math.PI) / 180;
    const φ1 = (center[0] * Math.PI) / 180;
    const λ1 = (center[1] * Math.PI) / 180;

    const sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1);
    const sinδ = Math.sin(δ), cosδ = Math.cos(δ);
    const sinθ = Math.sin(θ), cosθ = Math.cos(θ);

    const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * cosθ;
    const φ2 = Math.asin(sinφ2);
    const y = sinθ * sinδ * cosφ1;
    const x = cosδ - sinφ1 * sinφ2;
    const λ2 = λ1 + Math.atan2(y, x);

    const lat2 = (φ2 * 180) / Math.PI;
    const lng2 = ((λ2 * 180) / Math.PI + 540) % 360 - 180;
    return [lat2, lng2];
  }, [center, radius_m, radiusBearingDeg]);

  // 3) 조건부 렌더링 (훅 호출 뒤에 조건 처리)
  const canDrawPolygon = visible && positions.length >= 3;
  const canDrawCenter = showCenter && !!center;
  const canDrawRadius = showRadius && !!center && !!radiusTip;

  // 라벨 문자열 (radius_m 제공 시 자동 생성)
  const labelText = useMemo(() => {
    if (typeof radiusLabel === "string") return radiusLabel;
    if (Number.isFinite(radius_m)) {
      const v = Number(radius_m);
      // 1000m 이상이면 km로 표기
      if (v >= 1000) return `r = ${(v / 1000).toFixed(labelDigits)} km`;
      return `${v.toFixed(labelDigits)} m`;
    }
    return "";
  }, [radiusLabel, radius_m, labelDigits]);

  return (
    <>
      {canDrawPolygon && (
        <Polygon
          positions={positions}
          pathOptions={{
            color: strokeColor,
            weight: strokeWeight,
            fill: true,
            fillColor: strokeColor,
            fillOpacity,
          }}
          eventHandlers={onClick ? { click: onClick } : undefined}
        />
      )}

      {canDrawCenter && (
        <CircleMarker
          center={center}
          radius={4}
          pathOptions={{ color: radiusColor, weight: 2, fillColor: radiusColor, fillOpacity: 1 }}
        />
      )}

      {canDrawRadius && (
        <Polyline
          positions={[center, radiusTip]}
          pathOptions={{ color: radiusColor, weight: 2, dashArray: "4,4" }}
        >
          {showRadiusLabel && labelText && (
            <Tooltip
              permanent
              direction="center"     // 폴리라인 중앙에 표시
              offset={[0, 0]}        // 필요 시 조절: 예) [0, -8]
              opacity={1}
              className="radius-label--red"
            >
              {labelText}
            </Tooltip>
          )}
        </Polyline>
      )}
    </>
  );
}